import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { IStepValidation, WizardStep } from '../../../WizardStep';
import { IMessage, IMessageMatchedContact } from '../../../../../store/models/message.model';
import { IUserSessionData, UserType } from '../../../../../store/models/user.model';
import { Subject } from 'rxjs';
import { SessionService } from '../../../../../store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { DataSourceParams, ETableColumnType, ITableColumn } from '../../../../../store/models/table.model';
import { IContactItem } from '../../../../../store/models/contact.model';
import {
  ContactSelectorActionsComponent,
  IRedNoMatchContactSelectionEvent,
  RED_NO_MATCH_EVENT_NAME,
} from '../select-contact/contact-selector-actions/contact-selector-actions.component';
import { ApiDataSource } from '../../../../../datasources/api.datasrouce';
import { openDialog, openSnackbar } from '../../../../../store/actions/page.action';
import { ContactsService } from '../../../../../store/services/contacts.service';
import { WIZARD_COMPONENT, WizardComponent } from '../../../wizard.component';
import { EventService } from '../../../../../store/services/event.service';
import { Router } from '@angular/router';
import { environment } from '../../../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { IReducers } from '../../../../../store/reducers';
import { Button, ButtonColor, ButtonSize } from '../../../../../store/models/button.model';

@Component({
  selector: 'app-search-for-contact',
  templateUrl: './search-for-contact.component.html',
  styleUrls: ['./search-for-contact.component.scss'],
})
export class SearchForContactComponent
  implements OnInit, WizardStep, OnDestroy {
  static ID = 'search-for-contact';

  wizardData: { [p: string]: any };

  messages: IMessage[];
  componentReady = false;
  sessionData: IUserSessionData;
  accountId: number;

  matchedContact: IMessageMatchedContact;

  tableReady: boolean;

  selectMatchedContactButton: Button = {
    text: 'Select',
    color: ButtonColor.BLUE,
    size: ButtonSize.NORMAL,
    onClick: () => this.mergeContacts(this.matchedContact.id),
  };

  tableColumns: ITableColumn<IContactItem>[] = [
    {
      id: 'contact_firstname',
      title: 'First name',
    },
    {
      id: 'contact_lastname',
      title: 'Last name',
    },
    {
      id: 'contact_company',
      title: 'company',
    },
    {
      id: 'contact_email',
      title: 'Email address',
    },
    {
      id: 'select_action',
      title: 'Action',
      type: ETableColumnType.COMPONENT,
      componentProperties: {
        component: ContactSelectorActionsComponent,
        mapData: {
          contactId: {
            instance: 'column',
            target: 'contact_id',
          },
        },
      },
    },
  ];
  tableDataSourceParams: DataSourceParams;
  dataSource: ApiDataSource<IContactItem>;

  searchString: string;
  contactId: number;
  isContactKnownDuplicate: boolean;
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly sessionService: SessionService,
    private readonly contactsService: ContactsService,
    @Inject(WIZARD_COMPONENT) private readonly wizardComponent: WizardComponent,
    private readonly eventService: EventService,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly store$: Store<IReducers>,
  ) {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.sessionData = session;
        this.eventService.on<IRedNoMatchContactSelectionEvent>(RED_NO_MATCH_EVENT_NAME)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            selection => {
              if (!selection || !selection.contactId) {
                return;
              }

              this.mergeContacts(selection.contactId, selection.defaultSelected);
            },
          );
        this.componentReady = true;
      });
  }

  mergeContacts(contactId: number, keep: boolean = false) {
    this.store$.dispatch(openDialog({
      dialogData: {
        content: 'Are you sure?',
        title: 'Merge contacts',
        canClose: true,
        buttons: [
          {
            text: 'Yes',
            color: ButtonColor.GREEN,
            size: ButtonSize.NORMAL,
            onClick: (dialog) => {
              this.contactsService
                .mergeContacts(
                  this.contactId,
                  contactId,
                  keep,
                )
                .subscribe(
                  result => {
                    if (result && result.success) {
                      if(result.message_id) {
                        this.router.navigate(['/messages', 'view', result.message_id, {
                          from: 'inbox'
                        }]);
                        dialog.closeDialog();
                        this.wizardComponent.notifyFinish();
                        return; 
                      }
                      this.wizardComponent.notifyFinish();
                      dialog.close();
                    }
                  },
                  error =>
                    this.store$.dispatch(
                      openSnackbar({
                        snackbarData: {
                          data: {
                            type: 'error',
                            message: error.error,
                          },
                        },
                      }),
                    ),
                );
            },
          },
        ],
      },
    }));
  }

  ngOnInit(): void {
    this.getDefaultValue();
  }

  provideData(): any {
    return this.searchString;
  }

  validate(): Promise<IStepValidation> {
    return Promise.resolve(undefined);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  onFieldChange($event: string) {
    this.searchString = $event;
  }

  buildDataSourceParams() {
    this.tableDataSourceParams = {
      page: 1,
      pageSize: 10,
      sortAttr: 'id',
      sortDir: 'desc',
      filters: {
        search: this.searchString,
        account_id: this.sessionData.user_type === UserType.VSM ? this.accountId.toString() : this.sessionData.id.toString(),
      },
    };
  }

  getDefaultValue() {
    this.searchString = this.messages[0]?.contact_email;

    let url = `${environment.apiUrl}/v1/contacts`;

    if (this.sessionData.user_type === UserType.VSM) {
      url += `?account_id=${this.accountId}`;
    }

    this.dataSource = new ApiDataSource<IContactItem>(
      this.http,
      url,
    );
    this.tableReady = true;
  }

  updateSearch() {
    this.buildDataSourceParams();
    this.dataSource.loadData(this.tableDataSourceParams);
  }
}
